// TipTap WYSIWYG editor for broadcast body. Storage shape is ProseMirror
// JSON — `value` is a `BroadcastDoc` (root `doc`, paragraph/heading/list
// nodes only, text marks bold/italic/underline/link). The editor's
// extension set is configured so it can ONLY produce shapes the server's
// `BroadcastDocSchema` accepts.
//
// Toolbar: 8 formatting buttons (B, I, U, link, bullet list, ordered list,
// H1, H2). Icons are imported directly from lucide-react — these are
// feature-specific glyphs (formatting controls), not the curated `I`
// design-system map (per `frontend-design`'s "Feature-specific glyphs →
// lucide-react inside the feature's own file" rule).

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import type { Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Heading1 as Heading1Icon,
  Heading2 as Heading2Icon,
  UserRound as UserRoundIcon,
} from 'lucide-react';
import { Row, VisuallyHidden } from '@leanin/ui';
import { Toggle as ToggleButton } from '@leanin/ui/client';
import {
  MERGE_TAG_LABELS,
  MERGE_TAG_NAMES,
  type MergeTagName,
} from '@leanin/shared/email/merge-tags';
import { type BroadcastDoc } from '@/lib/queries/broadcasts';
import { MergeTag } from './MergeTag';

import styles from './BroadcastBodyEditor.module.css';

export type { BroadcastDoc };

// The full extension set, exported so tests can drive the exact document
// model the mounted editor uses (see MergeTag.test.ts). Everything here maps
// 1:1 onto a shape the server's `BroadcastDocSchema` accepts — including the
// `mergeTag` inline atom the email sender substitutes per recipient.
export function broadcastEditorExtensions(): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2] },
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      validate: (href) => /^(https?:|mailto:)/i.test(href),
    }),
    Placeholder.configure({ placeholder: 'Write your message…' }),
    MergeTag,
  ];
}

export interface BroadcastBodyEditorProps {
  readonly value: BroadcastDoc;
  readonly onChange: (doc: BroadcastDoc) => void;
  readonly disabled?: boolean;
}

export function BroadcastBodyEditor({
  value,
  onChange,
  disabled,
}: BroadcastBodyEditorProps) {
  // TipTap fires `onUpdate` once on mount when it dispatches the transaction
  // that installs the initial `content`. The editor's normalized JSON often
  // differs subtly from the stored JSON (e.g. empty `marks: []` arrays
  // stripped, attribute key order), so echoing that initial update back to
  // the parent would mark the form dirty before the user has typed.
  // `hasMounted` skips exactly the first update; all subsequent updates are
  // genuine user input.
  const hasMounted = useRef(false);

  const editor = useEditor({
    extensions: broadcastEditorExtensions(),
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }
      onChange(ed.getJSON() as BroadcastDoc);
    },
    editorProps: {
      attributes: {
        class: styles.canvas ?? '',
      },
    },
  });

  // Keep editor enable-state in sync with the disabled prop. The initial
  // value is set above; we don't push subsequent `value` updates back in to
  // avoid clobbering the editor's own caret/selection state on every
  // keystroke (re-render loop). The parent owns the doc; the editor is the
  // canonical writer while mounted.
  useEffect(() => {
    if (editor === null) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (editor === null) {
    return <div className={styles.shell} aria-busy="true" />;
  }

  return (
    <div className={styles.shell}>
      <Toolbar editor={editor} disabled={disabled === true} />
      <EditorContent editor={editor} className={styles.content} />
    </div>
  );
}

type ToolbarProps = {
  readonly editor: Editor;
  readonly disabled: boolean;
};

function Toolbar({ editor, disabled }: ToolbarProps) {
  return (
    <Row
      gap="xs"
      align="center"
      wrap
      className={styles.toolbar}
      role="toolbar"
      aria-label="Formatting"
    >
      <ToolbarButton
        label="Bold"
        active={editor.isActive('bold')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={editor.isActive('italic')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        active={editor.isActive('underline')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Link"
        active={editor.isActive('link')}
        disabled={disabled}
        onClick={() => promptForLink(editor)}
      >
        <LinkIcon size={16} strokeWidth={2} />
      </ToolbarButton>

      <span className={styles.divider} aria-hidden="true" />

      <ToolbarButton
        label="Bullet list"
        active={editor.isActive('bulletList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive('orderedList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrderedIcon size={16} strokeWidth={2} />
      </ToolbarButton>

      <span className={styles.divider} aria-hidden="true" />

      <ToolbarButton
        label="Heading 1"
        active={editor.isActive('heading', { level: 1 })}
        disabled={disabled}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 1 }).run()
        }
      >
        <Heading1Icon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Heading 2"
        active={editor.isActive('heading', { level: 2 })}
        disabled={disabled}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
      >
        <Heading2Icon size={16} strokeWidth={2} />
      </ToolbarButton>

      <span className={styles.divider} aria-hidden="true" />

      {MERGE_TAG_NAMES.map((name) => (
        <MergeTagButton
          key={name}
          name={name}
          editor={editor}
          disabled={disabled}
        />
      ))}
    </Row>
  );
}

// Inserts the merge-tag chip at the cursor, followed by a space so typing
// continues naturally after it. Without this button a leader who deletes
// the prefilled chip has no way to get personalization back.
function MergeTagButton({
  name,
  editor,
  disabled,
}: {
  readonly name: MergeTagName;
  readonly editor: Editor;
  readonly disabled: boolean;
}) {
  const label = MERGE_TAG_LABELS[name];
  return (
    <ToolbarButton
      label={`Insert ${label.toLowerCase()}`}
      active={editor.isActive('mergeTag', { name })}
      disabled={disabled}
      wide
      onClick={() =>
        editor
          .chain()
          .focus()
          .insertContent([
            { type: 'mergeTag', attrs: { name } },
            { type: 'text', text: ' ' },
          ])
          .run()
      }
    >
      <UserRoundIcon size={16} strokeWidth={2} />
      <span aria-hidden="true">{label}</span>
    </ToolbarButton>
  );
}

type ToolbarButtonProps = {
  readonly label: string;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
  // Sized to its icon + text content instead of the square 28px icon slot —
  // used by the labeled merge-tag button.
  readonly wide?: boolean;
};

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  children,
  wide = false,
}: ToolbarButtonProps) {
  return (
    <ToggleButton
      pressed={active}
      onPressedChange={onClick}
      disabled={disabled}
      aria-label={label}
      className={wide ? `${styles.btn} ${styles.btnWide}` : styles.btn}
      onMouseDown={(e) => {
        // Prevent the toolbar button from stealing focus from the editor —
        // otherwise the caret jumps and the formatting command runs against
        // an empty selection.
        e.preventDefault();
      }}
    >
      {children}
      <VisuallyHidden>{label}</VisuallyHidden>
    </ToggleButton>
  );
}

function promptForLink(editor: Editor) {
  // `window.prompt` is the lightest possible link-entry UI; a richer popover
  // can replace this later (the editor API stays the same — set/extend a
  // link mark on the current selection).
  const previous = (editor.getAttributes('link')['href'] as string | undefined) ?? '';
  const next = window.prompt('Link URL (https:// or mailto:)', previous);
  if (next === null) return; // user cancelled
  const trimmed = next.trim();
  if (trimmed.length === 0) {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  if (!/^(https?:|mailto:)/i.test(trimmed)) {
    window.alert('Link must start with http://, https://, or mailto:');
    return;
  }
  editor
    .chain()
    .focus()
    .extendMarkRange('link')
    .setLink({ href: trimmed })
    .run();
}

// Helper used by other consumers (composer subject field) to ensure the
// editor has a non-empty starting doc. Returning a one-paragraph empty doc
// matches what TipTap produces when the user has typed nothing, so the
// editor's initial state and "user typed nothing" round-trip identically.
export function emptyBroadcastDoc(): BroadcastDoc {
  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: [] }],
  } as BroadcastDoc;
}

